import React, { useState, useRef, useEffect } from 'react';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import _clone from 'lodash/cloneDeep';
import _filter from 'lodash/filter';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';

import { If } from 'utils';
import { UiKit } from 'components';
import { TableZeroState } from 'components/Tables';
import { useDebounce } from 'utils/hooks';
import { ReactComponent as ProductsCatalogIcon } from 'assets/ss-icons/zero-state-icons/products-catalog.svg';

/**
 * StaticListSelectionModal component for selecting items from a provided list
 * @param {Object} props
 * @param {Function} props.onComplete - Callback when selection is confirmed
 * @param {Function} props.onCancel - Callback when selection is cancelled
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Array} props.initialSelected - Initially selected items
 * @param {Array} props.listItems - List of items to display and select from
 * @param {string} props.title - Modal title
 * @param {string} props.idField - Field to use as unique identifier
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {string} props.searchField - Field to search in (defaults to name)
 * @param {Object} props.className - CSS class for the modal
 * @param {React.Component} props.ItemsListComponent - Component to render the list of items
 * @param {string} props.checkCountId - Optional ID for the count of selected items
 */
const StaticListSelectionModal = ({
  onComplete,
  onCancel,
  isOpen,
  initialSelected = [],
  listItems = [],
  title = 'Select Items',
  idField = 'ObjectId',
  searchPlaceholder = 'Search...',
  checkCountId,
  searchField = 'name',
  className,
  ItemsListComponent,
  showSelectAll = true
}) => {
  const [selected, setSelected] = useState({});
  const [filteredItems, setFilteredItems] = useState(listItems);
  const [searchValue, setSearchValue] = useState('');
  const initialItemsMap = useRef({});

  const searchQuery = useDebounce(searchValue);

  const canSave = _isEqual(selected, initialItemsMap.current);

  // Initialize selected items when modal opens
  useEffect(() => {
    if (isOpen) {
      const itemsMap = initialSelected.reduce((acc, item) => {
        const key = item[idField];
        acc[key] = {
          id: key,
          ...item
        };
        return acc;
      }, {});
      initialItemsMap.current = itemsMap;
      setSelected(itemsMap);
      setFilteredItems(listItems);
    } else {
      initialItemsMap.current = {};
      setSearchValue('');
      setSelected({});
    }
  }, [isOpen, initialSelected, idField, listItems]);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems(listItems);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = _filter(listItems, item => {
        const searchQuery = item[searchField];
        return searchQuery && searchQuery.toLowerCase().includes(query);
      });
      setFilteredItems(filtered);
    }
  }, [searchQuery, listItems, searchField]);

  const toggleSelection = item => {
    const key = item[idField];
    const itemsMap = _clone(selected);
    if (key in itemsMap) {
      _unset(itemsMap, key);
    } else {
      _set(itemsMap, key, {
        id: key,
        ...item
      });
    }
    setSelected(itemsMap);
  };

  const handleComplete = () => {
    const selectedItems = Object.values(selected).map(item => {
      const result = { ...item };
      result[idField] = item.id;
      return result;
    });
    onComplete(selectedItems);
  };

  const clearAll = () => setSelected({});

  return (
    <UiKit.Modal isOpen={isOpen} onClose={onCancel}>
      <UiKit.Modal.Header title={title} />
      <UiKit.Modal.Body className={className}>
        <div className="static-list-selection">
          <div className="mb-3">
            <UiKit.Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={setSearchValue}
              className="w-100"
            />
          </div>
          {showSelectAll && !searchQuery && (
            <div className="mb-3">
              <UiKit.Checkbox
                label="Select All"
                checked={Object.keys(selected).length === filteredItems.length}
                onChange={() => {
                  const newSelected = {};
                  if (Object.keys(selected).length !== filteredItems.length) {
                    filteredItems.forEach(item => {
                      const key = item[idField];
                      newSelected[key] = { id: key, ...item };
                    });
                  }
                  setSelected(newSelected);
                }}
              />
            </div>
          )}
          <If
            test={!_isEmpty(filteredItems)}
            otherwise={
              <TableZeroState
                searchQuery={searchQuery}
                icon={<ProductsCatalogIcon />}
              >
                There is no product type.
              </TableZeroState>
            }
          >
            {ItemsListComponent && (
              <ItemsListComponent
                items={filteredItems}
                toggle={toggleSelection}
                selected={selected}
              />
            )}
          </If>
        </div>
      </UiKit.Modal.Body>
      <UiKit.Modal.Footer
        onConfirm={handleComplete}
        isConfirmDisabled={canSave}
        confirmBtnText={
          checkCountId
            ? `Confirm (${Object.values(selected).reduce(
                (acc, item) => acc + item[checkCountId],
                0
              )})`
            : 'Confirm'
        }
      >
        <If test={!_isEmpty(selected)}>
          <UiKit.Button variant="ghost" onClick={clearAll}>
            Unselect All
          </UiKit.Button>
        </If>
      </UiKit.Modal.Footer>
    </UiKit.Modal>
  );
};

export default StaticListSelectionModal;
